import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Rating,
} from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * The student-facing results body (grade & feedback, submission, score
 * summary, received reviews). Shared between the tokenized student results
 * page and the instructor "preview as student" view — `children` is appended
 * after the reviews (the student page adds the reflection card there).
 */
export function StudentResultsView({
  results,
  children,
}: {
  results: any;
  children?: React.ReactNode;
}) {
  const [t] = useTranslation();

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      {/* Header */}
      <Typography variant="h5" sx={{ mb: 3 }}>
        {t("Your Peer Review Results")}
      </Typography>

      {results.activityDescription && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {results.activityDescription}
        </Typography>
      )}

      {/* Grade & Instructor Feedback */}
      {(results.submissionGrade != null || results.instructorFeedback) && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardHeader
            title={String(t("Instructor Grade & Feedback"))}
            titleTypographyProps={{ variant: "h6" }}
          />
          <CardContent>
            {results.submissionGrade != null && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mr: 2 }}>
                  {t("Grade")}:
                </Typography>
                <Chip
                  label={results.submissionGrade}
                  color="primary"
                  size="medium"
                />
              </Box>
            )}
            {results.instructorFeedback && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("Feedback")}:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    p: 2,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  {results.instructorFeedback}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Your Submission */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader
          title={String(t("Your Submission"))}
          titleTypographyProps={{ variant: "h6" }}
        />
        <CardContent>
          {results.submissionText ? (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {results.submissionText}
            </Typography>
          ) : (
            <Typography color="text.secondary">
              {t("No text submission.")}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Per-Criterion Score Summary */}
      {results.criterionSummaries && results.criterionSummaries.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardHeader
            title={String(t("Score Summary"))}
            titleTypographyProps={{ variant: "h6" }}
          />
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Criterion")}</TableCell>
                  <TableCell>{t("Average Score")}</TableCell>
                  <TableCell>{t("Reviews")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.criterionSummaries.map((cs: any) => (
                  <TableRow key={cs.itemId}>
                    <TableCell>{cs.label}</TableCell>
                    <TableCell>
                      {cs.averageScore != null ? (
                        <Chip
                          label={cs.averageScore.toFixed(1)}
                          size="small"
                          color="primary"
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{cs.scoreCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Peer Reviews Received */}
      <Card variant="outlined">
        <CardHeader
          title={`${String(t("Peer Reviews Received"))} (${
            results.reviews.length
          })`}
          titleTypographyProps={{ variant: "h6" }}
        />
        <CardContent>
          {results.reviews.length === 0 ? (
            <Typography color="text.secondary">
              {t("No peer reviews have been submitted yet.")}
            </Typography>
          ) : (
            results.reviews.map((review: any, index: number) => (
              <Box key={index}>
                {index > 0 && <Divider sx={{ my: 2 }} />}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    {review.reviewerName}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("Helpful?")}
                    </Typography>
                    <Rating size="small" />
                  </Box>
                </Box>
                {review.answers.length > 0 ? (
                  <Table size="small">
                    <TableBody>
                      {review.answers.map((answer: any) => (
                        <TableRow key={answer.id}>
                          <TableCell sx={{ border: "none", pl: 0 }}>
                            {answer.value != null && (
                              <Chip
                                label={answer.value}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            {answer.text && (
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: answer.value != null ? 0.5 : 0,
                                }}
                              >
                                {answer.text}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("No detailed feedback provided.")}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {children}
    </Box>
  );
}
